import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { useNavigate } from "react-router-dom";
import { JudgePhaseProjectType } from "../../../types/ScientificModule";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useGetJudgeExhibitorByUserIdQuery } from "../../../hooks/scientific/useGetJudgeExhibitorByUserIdQuery";
import { useGetProjectTypesQuery } from "../../../hooks/scientific/useGetProjectTypesQuery";
import { useGetEntitiesQuery } from "../../../hooks/scientific/useGetEntitiesQuery";
import toast from "react-hot-toast";
import { useState } from "react";
import { dataURLtoFile, getFile, getFileExt } from "../../../utils/fileUtils";
import { Box, Button, Card, Flex, Spinner, Tabs, Text } from "@radix-ui/themes";
import { ChevronLeftIcon, EnterIcon, PlusIcon } from "@radix-ui/react-icons";
import { EmptyState, ErrorState } from "../../../components/common/EmptyState";
import DateFieldComponent from "../../../components/inputs/Datefield/DatefieldComponent";
import { DragAndDropFileUpload } from "../../../components/inputs/FileUpload/DragAndDropFileUpload";
import SelectComponent from "../../../components/inputs/Select/SelectComponent";
import TextAreaFieldComponent from "../../../components/inputs/TextAreafield/TextAreafieldComponent";
import TextFieldComponent from "../../../components/inputs/Textfield/TextfieldComponent";
import { useGetPhasesQuery } from "../../../hooks/scientific/useGetPhasesQuery";
import { routes } from "../../../routes";
import { useAppDispatch } from "../../../hooks/hooks";
import { resetProjectsData } from "../../../redux/slices/scientific/projects";
import { createJudgePhaseProject } from "../../../services/scientific/judgePhaseProject";
import { useMutation } from "react-query";
import { createJudgePhaseProjectDocument } from "../../../services/scientific/judgePhaseProjectDocument";
import { toDateOnly } from "../../../utils/dateFormatter";
import { updateProject } from "../../../services/scientific/project";
type ActiveSegmentTab = 'Project' 
                        | 'Documents' 
                        | 'Phases' 
                        | 'Evaluate';
                        // | 'Comments' 

export const ValidateProjectStep = () => {
    const [activeSegment, setActiveSegment] = useState<ActiveSegmentTab>('Project');
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const projectData = useSelector((state: RootState) => state.projects);
    const userLogged = useSelector((state: RootState) => state.login.userData);
    const mutation = useMutation({ mutationFn: createJudgePhaseProject, mutationKey: 'createJudgePhaseProject' });
    const mutationUpdateProject = useMutation({ mutationFn: updateProject, mutationKey: 'updateProject' });
    const mutationCreateJudgePhaseDocument = useMutation({ mutationFn: createJudgePhaseProjectDocument, mutationKey: 'createJudgePhaseProjectDocument' });

    const loadingState = mutationCreateJudgePhaseDocument.isLoading || mutation.isLoading ||mutationUpdateProject.isLoading;

    const { data } = useGetJudgeExhibitorByUserIdQuery(userLogged.UserId, {
        enabled: !!userLogged.UserId
    })
    const { data: projectTypes, isLoading: isLoadingProjectTypes, isError: isErrorProjectTypes, refetch: refreshProjectTypes } = useGetProjectTypesQuery({ PageNumber: 1, PageSize: 400 });
    const { data: entities, isLoading: isLoadingEntities, isError: isErrorEntities, refetch: refreshEntities } = useGetEntitiesQuery({ PageNumber: 1, PageSize: 400 });
    const { data: phases, isLoading: isLoadingPhases, isError: isErrorPhases, refetch: refreshPhases } = useGetPhasesQuery({ PageNumber: 1, PageSize: 400, IdProjectType:projectData.idProjectType }, 
        {
            enabled:!!projectData.idProjectType
        }
    );

    const idJudge = data?.idJudge;
    const defaultValues: JudgePhaseProjectType = {
        id: "",
        comments: "",
        idJudge: idJudge!,
        idPhase: projectData.idCurrentPhase!,
        idProject: projectData.id!,
        project: {
            ...projectData,
            principalDocBase64File: projectData?.principalDocBase64 ? dataURLtoFile(projectData.principalDocBase64, getFileExt(projectData.principalDocBase64,"principal_doc.pdf")) : undefined,
            documents: projectData?.documents?.map((d, index) => {
                if (d.docBase64) {
                    const file = dataURLtoFile(d.docBase64, `doc_${index}.pdf`);
                    return { ...d, file, };
                }
                return d;
            }) ?? []
        }
    }

    const { control, handleSubmit, formState: { errors }, trigger } = useForm<JudgePhaseProjectType>({
        //    resolver: yupResolver(CreateProjectFormValidation),
        defaultValues,
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        shouldUseNativeValidation: false,
    });

    const onSubmit = handleSubmit(async (data) => {
        try {
           const  {idPhase, idJudge, idProject, documents, comments,startDate= toDateOnly(new Date()), endDate= toDateOnly(new Date())}  = data
           const phase = phases?.items.find(i=> i.id===idPhase);
           const phaseNext = phase?.idPhaseNext;
           const dataToSend : JudgePhaseProjectType = {
               idJudge,
               idPhase,
               idProject,
               startDate,
               endDate, 
               comments, 

           }
           const resultCreateJudgePhaseProject = await mutation.mutateAsync(dataToSend)
           if(!resultCreateJudgePhaseProject) return;
           if(data.documents && data.documents.length>0){
            const convertedToBase64Docs = await Promise.all(data.documents?.filter(d=> d.file).map(async (document) => {
              return getFile(document.file)
          }))
          
          await Promise.all(convertedToBase64Docs.map((cv) => {
              return mutationCreateJudgePhaseDocument.mutateAsync({
                docBase64: cv.base64,
                idJudgePhaseProject: resultCreateJudgePhaseProject
              })
          }))
          }
          let updateprojectData = {...projectData, idCurrentPhase: phaseNext};
          if(!phaseNext){
            updateprojectData.isEnded = true;
            updateprojectData.enddate = toDateOnly(new Date());
          }
          await mutationUpdateProject.mutateAsync(updateprojectData)
          toast.success('Submetido');
          handleBack();
        } catch (err: any) {
             toast.error(`${mutation.error ?? err?.message}`);
        }
    });
    const navigateAndValidate = (tabToNavigateTo: ActiveSegmentTab) => {
        if (activeSegment !== tabToNavigateTo) setActiveSegment(tabToNavigateTo);
        trigger();
    }
    const { fields } = useFieldArray({
        control,
        name: 'project.documents',
    });
    const { fields: fieldsJudgePhaseDocuments, append, remove } = useFieldArray({
        control,
        name: 'documents',
    });
    const projectPhases = phases?.items?.filter(i => i.idProjectType == projectData.idProjectType) ?? [];
    const handleBack = () => {
        dispatch(resetProjectsData());
        navigate(routes.projectsAssignedToJudge);
      };
    return (
        <form onSubmit={onSubmit} noValidate>
            <Tabs.Root value={activeSegment} onValueChange={(tabToNavigate: string) => navigateAndValidate(tabToNavigate as ActiveSegmentTab)}>
                <Tabs.List>
                    <Tabs.Trigger value="Project">
                        Dados do projecto
                    </Tabs.Trigger>
                    <Tabs.Trigger value="Documents">Documentos</Tabs.Trigger>
                    <Tabs.Trigger value="Evaluate">Minha Avaliação</Tabs.Trigger>
                    {/* <Tabs.Trigger value="Comments">Avaliações</Tabs.Trigger> */}
                    <Tabs.Trigger value="Phases">Fases</Tabs.Trigger>

                </Tabs.List>
                <Tabs.Content value="Project">
                    <Card size='1'>
                        <Flex direction='column' gap='4'>
                            <TextAreaFieldComponent name="project.description" label="Descrição" control={control} errors={errors} fieldProps={{ placeholder: 'Descrição', disabled:"disabled" }} />
                            <SelectComponent
                                name="project.idProjectType"
                                label="Tipo de projecto"
                                control={control}
                                errors={errors}
                                options={projectTypes?.items.map((projectType) => ({
                                    value: projectType.id ?? "",
                                    label: projectType.description ?? "",
                                }))}
                                itemsLoading={isLoadingProjectTypes}
                                itemsError={isErrorProjectTypes}
                                fieldProps={{disabled:"disabled" }}
                            />
                            <Flex gap='4'>
                                <DateFieldComponent name="project.startdate" label="Data de Início" control={control} errors={errors} style={{ width: '100%' }}   
                                                              fieldProps={{disabled:"disabled" }}
  />
                                <DateFieldComponent name="project.enddate" label="Data de Fim" control={control} errors={errors} style={{ width: '100%' }}   
                                                               fieldProps={{disabled:"disabled" }}
                                />
                            </Flex>
                            <Flex gap='4'>
                                <SelectComponent
                                    name="project.idEntity"
                                    label="Entitidade"
                                    control={control}
                                    errors={errors}
                                    options={entities?.items.map((entity) => ({
                                        value: entity.id ?? "",
                                        label: entity.description ?? "",
                                    }))}
                                    style={{ width: '100%' }}
                                    itemsLoading={isLoadingEntities}
                                    itemsError={isErrorEntities}
                                    fieldProps={{disabled:"disabled" }}

                                />
                                <TextFieldComponent name="project.entityName" label="Entidade" control={control} errors={errors} fieldProps={{ placeholder: 'Nome da Entidade', disabled:'disabled' }} style={{ width: '100%' }} />
                            </Flex>
                            <TextFieldComponent name="project.moneyInvested" label="Investimento" control={control} errors={errors} fieldProps={{ placeholder: 'Montante', disabled:'disabled' }} />
                            <TextAreaFieldComponent name="project.objectives" label="Objetivos" control={control} errors={errors} fieldProps={{ placeholder: 'Objetivos do projeto', disabled:'disabled'  }} />

                            <Box>
                                <Text as="label" size="2">Documento Principal</Text>
                                <Controller
                                    name="project.principalDocBase64File"
                                    control={control}
                                    render={({ field }) => (
                                        <DragAndDropFileUpload
                                            onFileDrop={(file) => {
                                                field.onChange(file);
                                            }}
                                            label={`Arraste ou seleccione o ficheiro`}
                                            accept="*"
                                            selectedFile={field.value}
                                            isReadOnly={true}
                                        />
                                    )}
                                />
                                {errors?.project?.principalDocBase64File && (
                                    <Text color="red" size="1">{`${errors?.project?.principalDocBase64File.message}`}</Text>
                                )}
                            </Box>
                        </Flex>
                    </Card>
                </Tabs.Content>
                <Tabs.Content value="Documents">
                    <Card size="2">
                        <Flex direction="column" gap="3">
                            <Text as="label" weight="medium">Documentos</Text>
                            {fields.length === 0 && (
                                <EmptyState
                                    title="Sem documentos"
                                    description="Este projecto não tem documentos de apoio."
                                />
                            )}
                            {fields.map((f, index) => (
                                <Box
                                    key={index}
                                    p="3"
                                    style={{
                                        border: '1px solid var(--gray-6)',
                                        borderRadius: 'var(--radius-3)',
                                        position: 'relative',
                                    }}
                                >
                                    <Controller
                                        name={`project.documents.${index}.file` as const}
                                        control={control}
                                        render={({ field }) => (
                                            <DragAndDropFileUpload
                                                onFileDrop={(file) => {
                                                    field.onChange(file);
                                                }}
                                                accept="*"
                                                selectedFile={field.value}
                                                isReadOnly={true}
                                            />
                                        )}
                                    />
                                    {errors.project?.documents?.[index]?.file && (
                                        <Text as="p" color="red" size="1" mt="1">
                                            {`${errors.project?.documents[index]?.message}`}
                                        </Text>
                                    )}
                                </Box>
                            ))}
                        </Flex>
                    </Card>
                </Tabs.Content>
                <Tabs.Content value="Evaluate">
                    <Flex gap='3' direction='column'>
                        <TextAreaFieldComponent name="comments" label="Comentário" control={control} errors={errors} fieldProps={{ placeholder: 'Comentário' }} />
                        <Card size="2">
                            <Flex direction="column" gap="3">
                                <Text as="label" weight="medium">Documentos</Text>
                                {fieldsJudgePhaseDocuments.map((f, index) => (
                                    <Box
                                        key={index}
                                        p="3"
                                        style={{
                                            border: '1px solid var(--gray-6)',
                                            borderRadius: 'var(--radius-3)',
                                            position: 'relative',
                                        }}
                                    >
                                        <Controller
                                            name={`documents.${index}.file` as const}
                                            control={control}
                                            render={({ field }) => (
                                                <DragAndDropFileUpload
                                                    onFileDrop={(file) => {
                                                        field.onChange(file);
                                                    }}
                                                    accept="*"
                                                    selectedFile={field.value}
                                                    remove={() => remove(index)}
                                                />
                                            )}
                                        />
                                        {errors.documents?.[index]?.file && (
                                            <Text as="p" color="red" size="1" mt="1">
                                                {`${errors.documents[index]?.message}`}
                                            </Text>
                                        )}
                                    </Box>
                                ))}
                                <Button type="button" variant="soft" style={{ alignSelf: 'start' }} onClick={() => append(
                                    {
                                        file: null,
                                        docBase64: ''
                                    }
                                )}>
                                    <PlusIcon /> Adicionar documento
                                </Button>
                            </Flex>
                        </Card>
                  <Flex direction='row' gap='3' justify='end'>
                        <Button type="button" variant='soft' color='gray' onClick={handleBack} disabled={loadingState}>
                            <ChevronLeftIcon /> Voltar
                        </Button>
                        <Button type="submit" disabled={loadingState}>
                            {loadingState ? <Spinner /> : <EnterIcon />}
                            {projectData?.PhaseNext ?? "Seguinte"}
                        </Button>
                        </Flex>


                    </Flex>
                </Tabs.Content>
                {/* <Tabs.Content value="Comments">
                </Tabs.Content> */}
                <Tabs.Content value="Phases">
                {isLoadingPhases ? (
            <Spinner />
          ) : isErrorPhases ? (
            <ErrorState title="Erro ao carregar as fases" onRetry={refreshPhases} />
          ) : (
            <Card size="2">
              <Flex direction="column" gap="3">
                {projectPhases.length === 0 && (
                  <EmptyState
                    title="Sem fases"
                    description="Este tipo de projecto ainda não tem fases definidas."
                  />
                )}
                {projectPhases.map((phase, index) => {
                  const isCurrent = phase.id === projectData.idCurrentPhase;

                  return (
                    <Box
                      key={phase.id}
                      p="3"
                      style={{
                        border: "1px solid var(--gray-6)",
                        borderRadius: "var(--radius-3)",
                        // Destaque para a fase actual
                        backgroundColor: isCurrent ? "var(--green-3)" : "var(--gray-2)",
                      }}
                    >
                      <Flex direction="column" gap="2">
                        <Text
                          size={isCurrent ? "4" : "2"}
                          weight={isCurrent ? "bold" : "regular"}
                          color={isCurrent ? "green" : "gray"}
                        >
                          {phase.description}
                        </Text>
                        <Text size="2">
                          <Text weight="bold">Fase Anterior:</Text> {phase.idPhasePrevious ? `${phase.phasePrevious}` : "N/D"}
                        </Text>
                        <Text size="2">
                          <Text weight="bold">Próxima Fase:</Text> {phase.idPhaseNext ? `${phase.phaseNext}` : "N/D"}
                        </Text>
                      </Flex>
                    </Box>
                  );
                })}
              </Flex>
            </Card>
          )}
                </Tabs.Content>
            </Tabs.Root>

        </form>
    )
}
